import { useQuery } from '@tanstack/react-query'
import RestDay from '../../model/RestDay'
import { dataSource } from '../../service/domain/_service.config'
import { useChallengeContext } from '../challenge/useChallengeContext'
import { useChallengeId } from '../challenge/useChallengeId'
import { useUser } from '../user/useUser'

export const REST_DAYS_QUERY_KEY = (userID: string, challengeID: string) =>
  ['restDays', userID, challengeID] as const

export interface RestDayStatus {
  restDays: RestDay[]
  activeRestDay: RestDay | null
  isActive: boolean
  count: number
  remaining: number
  canStart: boolean
  restDaysRequired: number | undefined
}

export function useRestDays() {
  const { data: user } = useUser()
  const challengeId = useChallengeId()
  const { data: challengeContext, isLoading: challengeLoading } = useChallengeContext()
  const restDaysRequired = challengeContext?.restDaysRequired

  const query = useQuery({
    queryKey: REST_DAYS_QUERY_KEY(user?.id ?? '', challengeId),
    queryFn: async () => {
      if (!user || !challengeId) return []
      return dataSource.getRestDays(user.id, challengeId)
    },
    enabled: !!user && !!challengeId,
  })

  const restDays = query.data ?? []
  const count = restDays.length
  let activeRestDay: RestDay | null = null
  let remaining = 0
  let canStart = false
  if (restDaysRequired !== undefined && restDaysRequired > 0) {
    activeRestDay = restDays.find((rd) => rd.isActive()) ?? null
    remaining = RestDay.getRemainingCount(count, restDaysRequired)
    canStart = RestDay.canStart(count, restDaysRequired)
  }

  return {
    ...query,
    restDays,
    activeRestDay,
    isActive: activeRestDay !== null,
    count,
    remaining,
    canStart,
    restDaysRequired,
    isLoading: query.isLoading || challengeLoading,
  }
}

/** Rest days for a specific user (e.g. selected team member). Use for displaying another member's rest day count. */
export function useRestDaysForUser(userId: string | undefined) {
  const challengeId = useChallengeId()

  const query = useQuery({
    queryKey: REST_DAYS_QUERY_KEY(userId ?? '', challengeId),
    queryFn: async () => {
      if (!userId || !challengeId) return []
      return dataSource.getRestDays(userId, challengeId)
    },
    enabled: !!userId && !!challengeId,
  })

  const count = (query.data ?? []).length

  return {
    ...query,
    count,
  }
}
